import { FC, FormEvent, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { PasswordFormElement, SubmitFormElement, ReCaptcha } from "../general";
import { ErrorWrapper } from "../wrappers";
import { auth, signInWithEmailAndPassword, signOut } from "../../firebase";
import { LoaderIcon } from "../../ui/icons";
import { DEFAULT_ERROR_WITH_SUGGESTION_MESSAGE } from "../../ui/constants";
import { useApi } from "../../api";

const SIGN_IN_AFTER_INVITE_FAILED_MESSAGE =
  "Please sign in with your email and new password.";

const INVALID_LINK_MESSAGE =
  "This invitation link is invalid or has expired. Please ask your company admin to send you a new invitation.";

type Phase = "recaptcha" | "verifying" | "setPassword" | "invalidLink";

export const InviteAcceptForm: FC = () => {
  const [searchParams] = useSearchParams();
  const [token] = useState(() => searchParams.get("token"));

  const [phase, setPhase] = useState<Phase>("recaptcha");
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const { getAnonymousApi } = useApi();

  useEffect(() => {
    if (!token) {
      setPhase("invalidLink");
      return;
    }
    if (!recaptchaToken) return;

    setPhase("verifying");
    setErrorMessage("");
    getAnonymousApi()
      .then((api) => api.verifyInviteToken({ token, recaptchaToken }))
      .then((response) => {
        setEmail(response.value.email);
        setPhase("setPassword");
      })
      .catch(() => setPhase("invalidLink"));
  }, [token, recaptchaToken, getAnonymousApi]);

  const handleSetPassword = async (event: FormEvent) => {
    event.preventDefault();
    if (!token) return;

    setErrorMessage("");
    setLoading(true);
    try {
      const api = await getAnonymousApi();
      await api.completeInvite({ token, recaptchaToken, password });
      // /user/invite/complete returns no session/token of any kind — the bff only sets
      // the password. We sign in ourselves with the email verifyInviteToken resolved and
      // the password just submitted, to get a real Firebase session before continuing.
      // Whoever opened this link might still have a different account signed in in this
      // browser (e.g. the admin who sent the invite, testing it themselves) — switching
      // straight to the new session without signing out first skips the app's
      // clear-stale-data-on-sign-out reset (src/store/store.ts, keyed to the null
      // transition onAuthStateChanged never sees during a direct account switch), leaving
      // the previous user's cached company/site data behind.
      // From here on the invite token is consumed, so a failure must not leave the
      // password form armed (a retry would re-call completeInvite and fail) — send the
      // user to sign in with the password they just set instead.
      try {
        await signOut(auth);
        await signInWithEmailAndPassword(auth, email, password);
      } catch {
        navigate("/auth/signin", {
          state: { errorMessage: SIGN_IN_AFTER_INVITE_FAILED_MESSAGE },
        });
        return;
      }
      navigate("/auth/complete-profile");
    } catch (error) {
      setErrorMessage(DEFAULT_ERROR_WITH_SUGGESTION_MESSAGE);
      setLoading(false);
    }
  };

  if (phase === "invalidLink") {
    return <ErrorWrapper errorMessage={INVALID_LINK_MESSAGE} />;
  }

  if (phase === "recaptcha") {
    return (
      <ErrorWrapper errorMessage={errorMessage}>
        <ReCaptcha
          errorMessage={errorMessage}
          setRecaptchaToken={setRecaptchaToken}
          actionName="invite_accept"
          setErrorMessage={setErrorMessage}
        />
      </ErrorWrapper>
    );
  }

  if (phase === "verifying") {
    return (
      <div className="flex justify-center">
        <LoaderIcon className="animate-spin" />
      </div>
    );
  }

  return (
    <ErrorWrapper errorMessage={errorMessage}>
      <form onSubmit={handleSetPassword}>
        <PasswordFormElement
          password={password}
          setValue={setPassword}
          currentStep={{
            name: "password",
            title: "Create password",
            type: "password",
            required: true,
          }}
        />
        <SubmitFormElement
          submitTitle={loading ? "Please wait…" : "Set password"}
          disabled={loading}
        />
      </form>
    </ErrorWrapper>
  );
};
